import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { formatCurrency, toBaseCurrency, formatDate, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { Wrench, Plus, Play, CheckCircle2, XCircle } from "lucide-react";

const STATUS = {
  open: { label: "Aberta", cls: "bg-amber-500/20 text-amber-600" },
  in_progress: { label: "Em Curso", cls: "bg-blue-500/20 text-blue-500" },
  done: { label: "Concluída", cls: "bg-emerald-500/20 text-emerald-600" },
  cancelled: { label: "Cancelada", cls: "bg-red-500/20 text-red-500" },
};

export default function MaintenancePage() {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [modal, setModal] = useState(false);
  const [completeModal, setCompleteModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [cost, setCost] = useState("");
  const [form, setForm] = useState({ target: "", type: "corrective", priority: "normal", scheduled_date: "", description: "" });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      const res = await axios.get(`${API_URL}/maintenance/orders`, { headers, withCredentials: true });
      setOrders(res.data);
    } catch { toast.error("Erro ao carregar manutenções"); }
  };

  const createOrder = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/maintenance/orders`, { ...form, scheduled_date: form.scheduled_date || null }, { headers, withCredentials: true });
      toast.success("Ordem de manutenção criada");
      setModal(false); setForm({ target: "", type: "corrective", priority: "normal", scheduled_date: "", description: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const updateStatus = async (order, status, costValue = 0) => {
    try {
      await axios.put(`${API_URL}/maintenance/orders/${order.maintenance_id}/status?status=${status}&cost=${costValue}`, {}, { headers, withCredentials: true });
      toast.success(status === "done" ? "Manutenção concluída (custo lançado no Financeiro)" : "Estado atualizado");
      setCompleteModal(false); setCost("");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const completeOrder = (e) => {
    e.preventDefault();
    updateStatus(selected, "done", toBaseCurrency(cost));
  };

  return (
    <div className="space-y-6" data-testid="maintenance-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">Manutenção</h1>
          <p className="text-muted-foreground">Ordens de manutenção preventiva e corretiva de equipamentos</p>
        </div>
        <Button onClick={() => setModal(true)} data-testid="add-maintenance-btn"><Plus className="w-4 h-4 mr-2" /> Nova Ordem</Button>
      </div>

      {orders.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground"><Wrench className="w-10 h-10 mx-auto mb-3 opacity-40" />Nenhuma ordem de manutenção</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {orders.map((o) => (
            <Card key={o.maintenance_id} data-testid={`maintenance-${o.maintenance_id}`}>
              <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-mono font-semibold">{o.number} <span className="font-sans font-normal">— {o.target}</span></p>
                  <p className="text-xs text-muted-foreground">
                    {o.type === "preventive" ? "Preventiva" : "Corretiva"} · Prioridade {o.priority === "high" ? "Alta" : o.priority === "low" ? "Baixa" : "Normal"}
                    {o.scheduled_date && ` · Prevista ${o.scheduled_date}`}
                    {o.cost > 0 && ` · Custo ${formatCurrency(o.cost)}`}
                    {` · ${formatDate(o.created_at)}`}
                  </p>
                  {o.description && <p className="text-xs text-muted-foreground">{o.description}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={STATUS[o.status]?.cls}>{STATUS[o.status]?.label || o.status}</Badge>
                  {o.status === "open" && (
                    <>
                      <Button size="sm" onClick={() => updateStatus(o, "in_progress")} data-testid={`start-maintenance-${o.maintenance_id}`}><Play className="w-4 h-4 mr-1" /> Iniciar</Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => updateStatus(o, "cancelled")}><XCircle className="w-4 h-4" /></Button>
                    </>
                  )}
                  {o.status === "in_progress" && (
                    <Button size="sm" onClick={() => { setSelected(o); setCompleteModal(true); }} data-testid={`complete-maintenance-${o.maintenance_id}`}>
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Concluir
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Nova Ordem de Manutenção</DialogTitle></DialogHeader>
          <form onSubmit={createOrder} className="space-y-3">
            <div className="space-y-1"><Label>Equipamento / Alvo *</Label><Input required value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} placeholder="Ex: Forno industrial, Ar condicionado" data-testid="maintenance-target-input" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label>Tipo</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="corrective">Corretiva</SelectItem>
                    <SelectItem value="preventive">Preventiva</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Prioridade</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Baixa</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1"><Label>Data Prevista</Label><Input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} /></div>
            <div className="space-y-1"><Label>Descrição</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-maintenance-btn">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={completeModal} onOpenChange={setCompleteModal}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle>Concluir — {selected?.number}</DialogTitle></DialogHeader>
          <form onSubmit={completeOrder} className="space-y-3">
            <div className="space-y-1"><Label>Custo da Manutenção</Label><Input type="number" step="0.01" min="0" value={cost} onChange={(e) => setCost(e.target.value)} data-testid="maintenance-cost-input" /></div>
            <p className="text-xs text-muted-foreground">Se indicar custo, será lançada uma despesa no Financeiro.</p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCompleteModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="confirm-complete-maintenance-btn">Concluir</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
